import React, { useState, useMemo } from 'react';

import { FormTitle, FormTitleAction } from '@components/admin/FormTitle';
import { Layout } from '@components/admin/Layout';
import { Button } from '@components/Button';
import { ExportCSVDialog } from '@components/ExportCSVDialog';
import { TagSearchInput } from '@components/Forms/TagSearchInput';
import { TagSearchInputMobxZodControl } from '@components/Forms/TagSearchInputStore';
import { PageSizeForm } from '@components/NextForms/PageSizeForm';
import { TextInput } from '@components/NextForms/TextInput';
import { PaginationWithSearchQuery } from '@components/PaginationWithSearchQuery';
import { Table, TableCellPropsUnion } from '@components/Table/Table';
import { allCompanyGrades, companyStrings } from '@data/company';
import { routePlanServiceNameStrings } from '@data/routePlan';
import { useSearch } from '@hooks/next/useSearch';
import { useMemberList } from '@hooks/queries/dashboard/useMemberList';
import { Member } from '@models/Member';
import { CompanyHelper, DashboardMemberSearchQuery } from '@models/next/Company';
import { ServiceName } from '@models/next/RoutePlan';
import { empty } from '@monoid-dev/mobx-zod-form';
import { useForm } from '@monoid-dev/mobx-zod-form-react';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { getFieldLabel } from '@utils/nextForms';
import { url } from '@utils/pages';
import { addOrdering, DEFAULT_PAGE_SIZE, formatDateTime } from '@utils/types';
import { useChecklist } from '@utils/useChecklist';
import { Link } from 'react-router-dom';
import { pick } from 'remeda';

const strings = createStrings({
  ja: {
    title: '会員一覧',
    searchTitle: '絞り込み',
    download: 'ダウンロード',
    todayCount: '今日の申込数',
    unassignedCount: '未対応数',
    assignedCount: '対応した件数',
    todayAssignedCount: '今日対応した件数',
    sectionTitle: '新規会員申し込み一覧',
    no: 'No.',
    companyName: '会社名',
    customerAssigneeName: '顧客側担当者名',
    assigneeName: '営業担当者',
    memberLevel: '顧客グレード',
    createdAt: '入会時間',
    memberNo: '会員番号',
    assessment: '顧客評価',
    unassigned: '未対応',
    details: '詳細',
    from: 'から',
    to: 'まで',
    exportCSV: 'Export CSV',
    fromTheNewTime: '時間順 新→古',
    fromTheOldTime: '時間順 古→新',
    selectOneItemAtLeast: '少なくとも1つのアイテムを選択してください',
    reset: 'リセット',
    tagSearchInputPlaceholder: '会員番号、会社名やメールなどの検索条件を入力してください',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexColumn,
    flex: 1,
    padding: '1.5rem 2rem',
  },
  tagSearchInputContainer: {
    ...common.middleText,
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr 30px) 30px repeat(2, 1fr 30px)',
    rowGap: 20,
    columnGap: 8,
    marginTop: '1rem',
    marginBottom: '1rem',
    backgroundColor: theme.palette.grey[50],
    padding: '1rem 1rem',
  },
  paginationContainer: {
    ...common.flexColumn,
    flex: 1,
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
  },
  buttonContainer: {
    ...common.flexInline,
    columnGap: '0.5rem',
  },
  linkDetails: {
    color: theme.palette.primary.main,
  },
  badge: {
    height: 8,
    width: 8,
    borderRadius: '50%',
    backgroundColor: theme.palette.warning.dark,
    position: 'absolute',
    left: 36,
    paddingBottom: 2,
    zIndex: 2,
  },
  searchTailLabel: {
    ...common.middleText,
    ...common.textGray700,
    ...common.textAlignCenter,
    paddingTop: 5,
  },
}));

const MembersSearchForm = () => {
  const { search, updateSearch } = useSearch(DashboardMemberSearchQuery);

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const form = useForm(DashboardMemberSearchQuery, {
    initialOutput: search,
    enableReinitialize: true,
  });

  const { fields } = form.root;

  const controller = useMemo(
    () =>
      new TagSearchInputMobxZodControl(
        pick(fields, ['member_no', 'company_name', 'salesperson_name', 'email', 'fullname']),
        form,
      ),
    [],
  );

  return (
    <form
      {...form.bindForm({
        onSubmit(d) {
          updateSearch({ ...d, page: 1 });
        },
      })}
      className={classes.tagSearchInputContainer}
    >
      <TagSearchInput
        controller={controller}
        placeholder={strings.tagSearchInputPlaceholder}
        styles={{
          wrapper: {
            gridColumn: '1 / -1',
          },
        }}
      />

      <TextInput
        field={fields.created_at_after}
        inputProps={{ placeholder: getFieldLabel(fields.created_at_after) }}
      />

      <label className={classes.searchTailLabel}>{formStrings.from}</label>

      <TextInput
        field={fields.created_at_before}
        inputProps={{ placeholder: getFieldLabel(fields.created_at_before) }}
      />

      <label className={classes.searchTailLabel}>{formStrings.until}</label>

      <div style={{ gridColumnEnd: '-1' }} />

      <div className={classes.buttonContainer}>
        <Button
          color="primary"
          shape="round"
          text={formStrings.filter}
          type="submit"
          size="small"
        />
        <Button
          color="primary"
          shape="round"
          text={formStrings.reset}
          type="submit"
          size="small"
          onClick={() => form.root.setOutput(empty)}
        />
      </div>
    </form>
  );
};

export const Members: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };
  const { search, updateSearch } = useSearch(DashboardMemberSearchQuery);

  const query = useMemo(() => {
    return {
      ...search,
      page: search.page ?? 1,
      page_size: search.page_size ?? DEFAULT_PAGE_SIZE,
      ordering: search.ordering ?? ['-created_at'],
    } satisfies DashboardMemberSearchQuery;
  }, [search]);

  const memberListQuery = useMemberList(query);

  const checklist = useChecklist(
    [],
    (memberListQuery.data?.results ?? []).map((r) => r.id),
  );
  const [exportDialogOpen, setExportDialogOpen] = useState<boolean>(false);

  const actions: FormTitleAction[] = [
    {
      title: strings.exportCSV,
      action: () => setExportDialogOpen(true),
    },
  ];

  const orderingOptions = [
    {
      value: '-created_at',
      label: strings.fromTheNewTime,
    },
    {
      value: 'created_at',
      label: strings.fromTheOldTime,
    },
  ];

  const { data, isLoading, error } = memberListQuery;

  const checkedItems = useMemo(() => {
    const items = (data?.results ?? [])
      .filter((item) => checklist.checked(item.id))
      .map((item) => item.id);
    return items;
  }, [data, checklist.checklist]);

  const headColumns: TableCellPropsUnion[] = [
    {
      label: 'checkbox',
      type: 'checkable',
      checked: checklist.allCheckedAndNotEmpty,
      onChange: checklist.toggleCheckAll,
    },
    {
      label: strings.no,
    },
    {
      label: strings.companyName,
    },
    {
      label: '',
    },
    {
      label: strings.customerAssigneeName,
    },
    {
      label: strings.assigneeName,
    },
    {
      label: companyStrings.customer_service_grades,
    },
    {
      label: strings.createdAt,
      type: 'selectable',
      options: orderingOptions,
      onChange: (value: any) => {
        updateSearch({
          page: 1,
          ordering: addOrdering(query.ordering, value),
        });
      },
    },
    {
      label: strings.memberNo,
    },
  ];

  function mapItemToTableRowData(
    index: number,
    item: Member,
    linkClassName: string,
  ): TableCellPropsUnion[] {
    return [
      {
        label: 'checkbox',
        type: 'checkable',
        checked: checklist.checked(item.id),
        onChange: (value: boolean) => checklist.setChecked(item.id, value),
      },
      {
        label: index + 1 + (query.page - 1) * query.page_size,
      },
      {
        label: (
          <div className={classes.preEllipsis} title={item.company_name || '-'}>
            {item.company_name || '-'}
          </div>
        ),
      },
      {
        label: (
          <span>
            {item.has_unverified_email && <div className={classes.badge}></div>}
            <Link
              to={`${url('/admin/members/MemberDetails')}?id=${item.id}`}
              className={linkClassName}
            >
              {strings.details}
            </Link>
          </span>
        ),
      },
      {
        label: `${item.user?.last_name} ${item.user?.first_name}`,
      },
      {
        label: item.salesperson_last_name || '-',
      },
      {
        label: (
          <span
            title={ServiceName.options
              .map(
                (option) =>
                  `${routePlanServiceNameStrings[option]}: ${
                    allCompanyGrades[item.customer_service_grades[option].grade]
                  }`,
              )
              .join('\n')}
          >
            {CompanyHelper.customerServiceGradesToText(item.customer_service_grades)}
          </span>
        ),
      },
      {
        label: formatDateTime(item.created_at),
      },
      {
        label: item.member_no || '-',
      },
    ];
  }

  const bodyRows: TableCellPropsUnion[][] | undefined = data?.results.map((result, index) => {
    return mapItemToTableRowData(index, result, classes.linkDetails);
  });

  return (
    <Layout title={strings.title} loading={isLoading} errors={[error]}>
      {() => (
        <>
          <ExportCSVDialog
            open={exportDialogOpen}
            onClose={() => {
              setExportDialogOpen(false);
            }}
            subject={strings.title}
            baseUrl="/dashboard/member/"
            query={query}
            selectedIds={checkedItems}
          />
          <div className={classes.container}>
            <FormTitle
              title={strings.title}
              actions={actions}
              titleSize="large"
              lineDivider={false}
            />

            <MembersSearchForm />

            <PageSizeForm />

            <div style={{ height: 16 }} />

            <Table columnFixedUntil={4} fixedWidths={[60, 60, 200, 80]}>
              <Table.Head>
                <Table.Row columns={headColumns} />
              </Table.Head>
              <Table.Body>
                {bodyRows?.map((columns, index) => {
                  return <Table.Row key={index} columns={columns} />;
                })}
              </Table.Body>
            </Table>
            <br />
            <div className={classes.paginationContainer}>
              <PaginationWithSearchQuery query={memberListQuery} pageSize={query.page_size} />
            </div>
          </div>
        </>
      )}
    </Layout>
  );
};
